// src/domains/core/dashboard/hooks/useCodeQualityMatrix.ts

import type {
  CodeQualityMatrixData,
  WorstFile,
  CodeQualityMetric,
  CachedCodeQualityData,
} from "../types/codeQuality.types";

import { core } from "@tauri-apps/api";
import { useState, useEffect, useCallback } from "react";

import { debugLog } from "@/domains/logging/utils/debugLog";

const CACHE_KEY = "tracq-code-quality-matrix-cache";
const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour

// API Response Types
interface CommentDensityFile {
  density?: number;
  path: string;
  status: string;
}

interface CommentDensityResponse {
  all_files?: CommentDensityFile[];
}

interface ModularityFile {
  complexity?: number;
  path: string;
  risk_score?: number;
}

interface ModularityResponse {
  data?: {
    _dir?: {
      files?: ModularityFile[];
    };
  };
  success?: boolean;
}

interface ModularityConfigResponse {
  [key: string]: unknown;
  data?: unknown;
}

/**
 * Load cached data from localStorage
 *
 * @returns Cached code quality data or null if cache is invalid or missing
 *
 */
const loadCachedData = (): CodeQualityMatrixData | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) {
      return null;
    }

    const parsed = JSON.parse(cached) as CachedCodeQualityData;
    const now = Date.now();
    const age = now - parsed.timestamp;

    // Check if cache is still valid
    if (age > CACHE_DURATION_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return parsed.data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    debugLog("error", "core", "Failed to load cached code quality data", {
      error: errorMessage,
      file: "useCodeQualityMatrix.ts",
      function: "loadCachedData",
      module: "dashboard",
    });
    return null;
  }
};

/**
 * Save data to localStorage with timestamp
 *
 * @param data
 *
 */
const saveCachedData = (data: CodeQualityMatrixData): void => {
  try {
    const cached: CachedCodeQualityData = {
      data: {
        ...data,
        lastUpdated: Date.now(),
      },
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    debugLog("error", "core", "Failed to save cached code quality data", {
      error: errorMessage,
      file: "useCodeQualityMatrix.ts",
      function: "saveCachedData",
      module: "dashboard",
    });
  }
};

/**
 * Hook for fetching and aggregating code quality metrics
 * Runs analyses in parallel and returns top 3 worst files per metric type
 * Caches results in localStorage for 1 hour
 *
 * @returns Object containing data, loading state, error state, and refetch function
 *
 */
export function useCodeQualityMatrix() {
  const [data, setData] = useState<CodeQualityMatrixData>(() => {
    // Try to load from cache first
    const cached = loadCachedData();
    if (cached) {
      return cached;
    }
    return {
      errors: {
        comment: null,
        complexity: null,
        issues: null,
        modularity: null,
      },
      loading: {
        comment: true,
        complexity: true,
        issues: true,
        modularity: true,
      },
      worstFilesByMetric: {
        comment: [],
        complexity: [],
        issues: [],
        modularity: [],
      },
    };
  });

  const analyzeCommentDensity = useCallback(async () => {
    try {
      setData((previous) => ({
        ...previous,
        errors: { ...previous.errors, comment: null },
        loading: { ...previous.loading, comment: true },
      }));

      // Get default config
      const defaultConfig = await core.invoke("get_default_config");

      const request = {
        config: defaultConfig,
        exclude_patterns: [],
        include_details: true,
        include_patterns: ["**"],
        roots: ["src", "src-tauri/src"],
        use_defaults: false,
      };

      const result = await core.invoke<CommentDensityResponse>("analyze_comment_density_dirs", {
        request,
      });

      if (result?.all_files) {
        // Sort by density (ascending - lowest density = worst)
        type SeverityType = "critical" | "high" | "low" | "medium";
        const filteredFiles = result.all_files.filter(
          (f: CommentDensityFile) => f.status === "Success" && f.density !== undefined,
        );
        // Reason: toSorted() is not recognized by TypeScript, but is available in runtime
        const sortedFiles = (
          filteredFiles as CommentDensityFile[] & {
            toSorted: (
              compareFn: (a: CommentDensityFile, b: CommentDensityFile) => number,
            ) => CommentDensityFile[];
          }
        ).toSorted(
          (a: CommentDensityFile, b: CommentDensityFile) => (a.density ?? 0) - (b.density ?? 0),
        );

        const worstFiles: WorstFile[] = sortedFiles
          .slice(0, 3)
          .map((file: CommentDensityFile, index: number) => {
            const density = file.density ?? 0;
            let severity: SeverityType;
            if (density < 5) {
              severity = "critical";
            } else if (density < 10) {
              severity = "high";
            } else if (density < 15) {
              severity = "medium";
            } else {
              severity = "low";
            }

            return {
              displayValue: `${density.toFixed(1)}%`,
              metric: "comment" as CodeQualityMetric,
              path: file.path,
              rank: index + 1,
              severity,
              value: density,
            };
          });

        setData((previous) => {
          const updated = {
            ...previous,
            loading: { ...previous.loading, comment: false },
            worstFilesByMetric: {
              ...previous.worstFilesByMetric,
              comment: worstFiles,
            },
          };
          // Save to cache when all analyses are complete
          if (!updated.loading.modularity && !updated.loading.complexity) {
            saveCachedData(updated);
          }
          return updated;
        });
      }
    } catch (error) {
      setData((previous) => ({
        ...previous,
        errors: {
          ...previous.errors,
          comment: error instanceof Error ? error.message : "Comment density analysis failed",
        },
        loading: { ...previous.loading, comment: false },
      }));
    }
  }, []);

  const analyzeModularity = useCallback(async () => {
    try {
      setData((previous) => ({
        ...previous,
        errors: { ...previous.errors, complexity: null, modularity: null },
        loading: { ...previous.loading, complexity: true, modularity: true },
      }));

      // Get default config
      const defaultConfigResult =
        await core.invoke<ModularityConfigResponse>("get_modularity_config");
      const defaultConfig = defaultConfigResult.data ?? defaultConfigResult;

      const request = {
        config: defaultConfig,
        include_patterns: ["**"],
        roots: ["src", "src-tauri/src"],
      };

      const result = await core.invoke<ModularityResponse>("analyze_modularity_dirs", {
        request,
      });

      if (result && result.success && result.data?._dir) {
        const files = result.data._dir.files ?? [];

        // Modularity: Sort by risk_score (descending - highest risk = worst)
        type SeverityType = "critical" | "high" | "low" | "medium";
        const filteredModularity = files.filter((f: ModularityFile) => f.risk_score !== undefined);
        // Reason: toSorted() is not recognized by TypeScript, but is available in runtime
        const sortedModularity = (
          filteredModularity as ModularityFile[] & {
            toSorted: (
              compareFn: (a: ModularityFile, b: ModularityFile) => number,
            ) => ModularityFile[];
          }
        ).toSorted(
          (a: ModularityFile, b: ModularityFile) => (b.risk_score ?? 0) - (a.risk_score ?? 0),
        );

        const worstModularity: WorstFile[] = sortedModularity
          .slice(0, 3)
          .map((file: ModularityFile, index: number) => {
            const riskScore = file.risk_score ?? 0;
            let severity: SeverityType;
            if (riskScore >= 8) {
              severity = "critical";
            } else if (riskScore >= 6) {
              severity = "high";
            } else if (riskScore >= 4) {
              severity = "medium";
            } else {
              severity = "low";
            }

            return {
              displayValue: riskScore.toFixed(2),
              metric: "modularity" as CodeQualityMetric,
              path: file.path,
              rank: index + 1,
              severity,
              value: riskScore,
            };
          });

        // Complexity: Sort by complexity (descending - highest complexity = worst)
        const filteredComplexity = files.filter((f: ModularityFile) => f.complexity !== undefined);
        // Reason: toSorted() is not recognized by TypeScript, but is available in runtime
        const sortedComplexity = (
          filteredComplexity as ModularityFile[] & {
            toSorted: (
              compareFn: (a: ModularityFile, b: ModularityFile) => number,
            ) => ModularityFile[];
          }
        ).toSorted(
          (a: ModularityFile, b: ModularityFile) => (b.complexity ?? 0) - (a.complexity ?? 0),
        );

        const worstComplexity: WorstFile[] = sortedComplexity
          .slice(0, 3)
          .map((file: ModularityFile, index: number) => {
            const complexity = file.complexity ?? 0;
            let severity: SeverityType;
            if (complexity >= 20) {
              severity = "critical";
            } else if (complexity >= 15) {
              severity = "high";
            } else if (complexity >= 10) {
              severity = "medium";
            } else {
              severity = "low";
            }

            return {
              displayValue: complexity.toString(),
              metric: "complexity" as CodeQualityMetric,
              path: file.path,
              rank: index + 1,
              severity,
              value: complexity,
            };
          });

        setData((previous) => {
          const updated = {
            ...previous,
            loading: {
              ...previous.loading,
              complexity: false,
              modularity: false,
            },
            worstFilesByMetric: {
              ...previous.worstFilesByMetric,
              complexity: worstComplexity,
              modularity: worstModularity,
            },
          };
          // Save to cache when all analyses are complete
          if (!updated.loading.comment) {
            saveCachedData(updated);
          }
          return updated;
        });
      }
    } catch (error) {
      setData((previous) => ({
        ...previous,
        errors: {
          ...previous.errors,
          complexity: error instanceof Error ? error.message : "Complexity analysis failed",
          modularity: error instanceof Error ? error.message : "Modularity analysis failed",
        },
        loading: { ...previous.loading, complexity: false, modularity: false },
      }));
    }
  }, []);

  const analyzeCodeQuality = useCallback(() => {
    // Code Quality analysis removed - feature no longer available
    setData((previous) => ({
      ...previous,
      errors: {
        ...previous.errors,
        issues: "Code quality analysis feature has been removed",
      },
      loading: { ...previous.loading, issues: false },
    }));
  }, []);

  // Start all analyses in parallel on mount (only if no valid cache)
  useEffect(() => {
    const cached = loadCachedData();
    if (cached) {
      // Use cached data, but still check if we need to refresh in background
      setData(cached);
      // Optionally refresh in background if cache is older than 30 minutes
      const cacheAge = Date.now() - (cached.lastUpdated ?? 0);
      if (cacheAge > 30 * 60 * 1000) {
        // Refresh in background without showing loading state
        analyzeCodeQuality();
        const refreshPromise = Promise.all([analyzeCommentDensity(), analyzeModularity()]);
        const handleRefreshError = (error: unknown): void => {
          const errorMessage = error instanceof Error ? error.message : String(error);
          debugLog("error", "core", "Failed to refresh code quality analyses", {
            error: errorMessage,
            file: "useCodeQualityMatrix.ts",
            function: "useEffect",
            module: "dashboard",
          });
        };
        // Reason: Promise error handler is intentionally not awaited, void operator marks it as intentionally ignored
        refreshPromise.catch(handleRefreshError);
      }
    } else {
      // No cache, run analyses
      analyzeCodeQuality();
      const analysesPromise = Promise.all([analyzeCommentDensity(), analyzeModularity()]);
      const handleStartError = (error: unknown): void => {
        const errorMessage = error instanceof Error ? error.message : String(error);
        debugLog("error", "core", "Failed to start code quality analyses", {
          error: errorMessage,
          file: "useCodeQualityMatrix.ts",
          function: "useEffect",
          module: "dashboard",
        });
      };
      // Reason: Promise error handler is intentionally not awaited, void operator marks it as intentionally ignored
      analysesPromise.catch(handleStartError);
    }
  }, [analyzeCommentDensity, analyzeModularity, analyzeCodeQuality]);

  const isLoading = Object.values(data.loading).some(Boolean);
  const hasError = Object.values(data.errors).some((error) => error !== null);

  return {
    data,
    hasError,
    isLoading,
    refetch: useCallback(() => {
      // Clear cache and force refresh
      localStorage.removeItem(CACHE_KEY);
      analyzeCodeQuality();
      const refetchPromise = Promise.all([analyzeCommentDensity(), analyzeModularity()]);
      const handleRefetchError = (error: unknown): void => {
        const errorMessage = error instanceof Error ? error.message : String(error);
        debugLog("error", "core", "Failed to refetch code quality analyses", {
          error: errorMessage,
          file: "useCodeQualityMatrix.ts",
          function: "refetch",
          module: "dashboard",
        });
      };
      // Reason: Promise error handler is intentionally not awaited, void operator marks it as intentionally ignored
      refetchPromise.catch(handleRefetchError);
    }, [analyzeCommentDensity, analyzeModularity, analyzeCodeQuality]),
  };
}
